import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Bot, X, Send, Mic, Loader2, Square, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUGERENCIAS = [
    '¿Quien me debe mas?',
    'Resumen general',
    '¿Cuanto stock tengo?',
    '¿Cuantos cortes abiertos hay?',
    'Top 5 deudores',
    'Ventas de hoy'
];

export const AsistenteIA = () => {
    const { isAdmin } = useAuth();
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [recording, setRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const [sessionId] = useState(() => `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    if (!isAdmin) return null;

    const enviar = async (texto) => {
        const q = (texto || input).trim();
        if (!q || loading) return;
        setMessages(m => [...m, { role: 'user', text: q }]);
        setInput('');
        setLoading(true);
        try {
            const r = await axios.post(`${API_URL}/api/asistente/preguntar`, {
                pregunta: q,
                session_id: sessionId
            });
            const resp = r.data?.respuesta || 'Sin respuesta';
            setMessages(m => [...m, { role: 'assistant', text: resp, tools: r.data?.tools_usadas || [] }]);
        } catch (e) {
            const msg = e.response?.data?.detail || e.message;
            setMessages(m => [...m, { role: 'assistant', text: `Error: ${msg}`, isError: true }]);
        } finally {
            setLoading(false);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mr = new MediaRecorder(stream);
            audioChunksRef.current = [];
            mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
            mr.onstop = async () => {
                stream.getTracks().forEach(t => t.stop());
                const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                if (blob.size === 0) return;
                setTranscribing(true);
                try {
                    const fd = new FormData();
                    fd.append('file', blob, 'audio.webm');
                    const r = await axios.post(`${API_URL}/api/asistente/transcribir`, fd, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    const texto = (r.data?.texto || '').trim();
                    if (texto) {
                        await enviar(texto);
                    } else {
                        toast.info('No se detecto voz en la grabacion');
                    }
                } catch (e) {
                    toast.error('Error transcribiendo: ' + (e.response?.data?.detail || e.message));
                } finally {
                    setTranscribing(false);
                }
            };
            mr.start();
            mediaRecorderRef.current = mr;
            setRecording(true);
        } catch (e) {
            toast.error('No se pudo acceder al microfono');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && recording) {
            mediaRecorderRef.current.stop();
            setRecording(false);
        }
    };

    return (
        <>
            {/* Boton flotante */}
            {!open && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-amber-400 text-zinc-900 shadow-2xl shadow-amber-400/30 hover:bg-amber-300 transition flex items-center justify-center group"
                    title="Asistente IA"
                    data-testid="btn-asistente-toggle"
                >
                    <Bot className="w-7 h-7 group-hover:scale-110 transition" />
                    <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 animate-pulse" />
                </button>
            )}

            {/* Panel */}
            {open && (
                <div className="fixed bottom-5 right-5 z-50 w-[min(420px,calc(100vw-1.5rem))] h-[min(640px,calc(100vh-2.5rem))] bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl flex flex-col" data-testid="asistente-panel">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-amber-400/20 border border-amber-400/40 flex items-center justify-center">
                                <Sparkles className="w-4 h-4 text-amber-400" />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-zinc-100">Asistente Karry Jean's</p>
                                <p className="text-[10px] text-zinc-500">Pregunta sobre tu negocio</p>
                            </div>
                        </div>
                        <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-zinc-200" data-testid="btn-asistente-close">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Mensajes */}
                    <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="asistente-mensajes">
                        {messages.length === 0 && (
                            <div className="text-center py-6">
                                <Bot className="w-12 h-12 text-amber-400/40 mx-auto mb-3" />
                                <p className="text-sm text-zinc-400 mb-1">Hola, soy tu asistente.</p>
                                <p className="text-xs text-zinc-500 mb-4">Pregunta lo que quieras sobre tu negocio.</p>
                                <div className="flex flex-wrap gap-1.5 justify-center px-2">
                                    {SUGERENCIAS.map((s, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => enviar(s)}
                                            className="text-[11px] px-2.5 py-1 rounded-full bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-amber-300 border border-zinc-700 transition"
                                            data-testid={`sugerencia-${i}`}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${m.role}-${i}`}>
                                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                                    m.role === 'user'
                                        ? 'bg-amber-400 text-zinc-900 rounded-tr-sm'
                                        : m.isError
                                            ? 'bg-red-500/10 border border-red-500/30 text-red-300 rounded-tl-sm'
                                            : 'bg-zinc-800 text-zinc-100 rounded-tl-sm border border-zinc-700'
                                }`}>
                                    {m.text}
                                    {m.tools && m.tools.length > 0 && (
                                        <p className="text-[9px] text-zinc-500 mt-1.5 italic">Consultado: {m.tools.join(', ')}</p>
                                    )}
                                </div>
                            </div>
                        ))}
                        {(loading || transcribing) && (
                            <div className="flex justify-start">
                                <div className="bg-zinc-800 border border-zinc-700 rounded-2xl rounded-tl-sm px-3 py-2 text-sm text-zinc-400 flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    {transcribing ? 'Transcribiendo...' : 'Pensando...'}
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <div className="p-3 border-t border-zinc-800">
                        <form onSubmit={(e) => { e.preventDefault(); enviar(); }} className="flex items-center gap-2">
                            <input
                                ref={inputRef}
                                type="text"
                                value={input}
                                onChange={e => setInput(e.target.value)}
                                placeholder={recording ? 'Grabando...' : 'Escribe tu pregunta...'}
                                disabled={loading || recording || transcribing}
                                className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-amber-400 disabled:opacity-50"
                                data-testid="asistente-input"
                            />
                            {input.trim() ? (
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    size="sm"
                                    className="bg-amber-400 hover:bg-amber-500 text-zinc-900 h-9 w-9 p-0"
                                    data-testid="btn-asistente-enviar"
                                >
                                    <Send className="w-4 h-4" />
                                </Button>
                            ) : (
                                <Button
                                    type="button"
                                    disabled={loading || transcribing}
                                    onClick={recording ? stopRecording : startRecording}
                                    size="sm"
                                    className={`h-9 w-9 p-0 ${recording ? 'bg-red-500 hover:bg-red-600 animate-pulse' : 'bg-amber-400 hover:bg-amber-500 text-zinc-900'}`}
                                    title={recording ? 'Detener grabacion' : 'Grabar pregunta por voz'}
                                    data-testid="btn-asistente-mic"
                                >
                                    {recording ? <Square className="w-4 h-4 text-white" /> : <Mic className="w-4 h-4" />}
                                </Button>
                            )}
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default AsistenteIA;
